'use client'

import { useState, useEffect } from 'react'
import { NextFont } from 'next/dist/compiled/@next/font'
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { useAppSetupStore } from './appSetup'

import { RatesProvider } from '@/context/rates-provider'
import { LayoutProvider } from '@/context/LayoutCtx'
import { type RegionsType } from '@/types/regions'
import { type TenantsType } from '@/consts/setupApp/tenants'
import { mappTenant } from '@/consts/tenants/front'
import { SessionExpiresProvider } from '@/context/SessionExpired'
import getAppFont from '@/theme/Fonts'
import getCustomTheme from '@/theme'

const queryClient = new QueryClient()

export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)

  if (parts.length === 2) return parts.pop()?.split(';').shift() ?? null

  return null
}

export function Providers({ children }: { children: React.ReactNode }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [theme, setTheme] = useState<Record<string, any>>()
  const [appFont, setAppFont] = useState<NextFont>()
  const handleSetup = useAppSetupStore((state) => state.handleSetup)

  useEffect(() => {
    const tenant = getCookie('tenant') as TenantsType | undefined
    const region = getCookie('region')?.toLocaleLowerCase() as RegionsType | undefined

    if (!region || !tenant) return

    const initialize = async () => {
      const tenantMapped = mappTenant[tenant] || tenant
      const appFont = getAppFont(tenantMapped)

      const theme = getCustomTheme({ tenant, region, fontFamily: appFont.style.fontFamily })

      setTheme(theme)
      setAppFont(appFont)
      handleSetup({ tenant })
    }

    initialize()
  }, [])

  if (!appFont) return null

  return (
    <ChakraProvider theme={theme}>
      <SessionExpiresProvider>
        <QueryClientProvider client={queryClient}>
          <ColorModeScript initialColorMode={'light'} />
          <RatesProvider>
            <LayoutProvider>
              <main className={appFont.className}>{children}</main>
            </LayoutProvider>
          </RatesProvider>
        </QueryClientProvider>
      </SessionExpiresProvider>
    </ChakraProvider>
  )
}
